import React, { useCallback, useMemo } from "react";

import { Box, Checkbox, FormControlLabel } from "@mui/material";

import { TreeViewItem } from "./components/TreeViewItem/TreeViewItem";
import { useTeamsFiles } from "@/hooks/useTeamsFiles";
import { TeamsFile } from "@/util/interfaces/TeamsFile";
import { buildTreeFromSelectedFiles, TreeNodeWithChildren } from "@/utils/treeBuilder";

type TTreeViewProps = {
  showSelectAll?: boolean;
  showDivider?: boolean;
};

// Internal tree node type
type TreeNode = TreeNodeWithChildren<TreeNode> & {
  selected: boolean;
};

export const TreeView: React.FC<TTreeViewProps> = ({ showSelectAll, showDivider }) => {
  const {
    selectedRows,
    getTeamsFilesByParentId,
    getSelectedFiles,
    updateTeamsFile,
    getTeamsFileById,
    teamsFiles: _teamsFiles,
  } = useTeamsFiles();

  // Build internal tree using the utility
  const treeNodes = useMemo(() => {
    const selectedFiles = getSelectedFiles();

    return buildTreeFromSelectedFiles<TreeNode>(
      selectedFiles,
      getTeamsFilesByParentId,
      getTeamsFileById,
      (file, level) => ({
        id: file.id,
        name: file.name,
        folder: !!file.folder,
        category: file.category || null,
        selected: file.selected,
        level,
      })
    );
  }, [getSelectedFiles, getTeamsFilesByParentId, getTeamsFileById]);
  // Helper function to find a node in the internal tree
  const findNodeInTree = useCallback((nodeId: string, nodes: TreeNode[]): TreeNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      const found = findNodeInTree(nodeId, node.children);
      if (found) return found;
    }
    return null;
  }, []);

  // Helper function to find children of a node in the internal tree
  const findChildrenInTree = useCallback(
    (parentId: string, nodes: TreeNode[]): TreeNode[] => {
      const parent = findNodeInTree(parentId, nodes);
      return parent ? parent.children : [];
    },
    [findNodeInTree]
  );

  // Function to update parent selection based on its children
  const updateParentSelectionRecursively = useCallback(
    (parentId: string, childId?: string, childWillBeSelected?: boolean) => {
      const parent = getTeamsFileById(parentId);
      if (!parent?.folder) return;

      // KEY: Only work with children that are in our internal tree
      const treeChildren = findChildrenInTree(parentId, treeNodes);

      // If there are no children in the tree, do nothing
      if (treeChildren.length === 0) {
        // But still continue upward just in case
        if (parent.parentReference?.id) {
          updateParentSelectionRecursively(parent.parentReference.id, parentId, parent.selected);
        }
        return;
      }

      // Check if all TREE children are selected
      const allTreeChildrenSelected = treeChildren.every((child) => {
        // If this child is the one we're updating, use the future value
        if (childId && child.id === childId) {
          return childWillBeSelected ?? child.selected;
        }
        // For other children, use their current state
        return child.selected;
      });

      // Only update if the parent's state needs to change
      if (parent.selected !== allTreeChildrenSelected) {
        updateTeamsFile(parentId, { selected: allTreeChildrenSelected });

        // IMPORTANT: Continue recursively upward
        // The parent's parent also needs to be checked
        if (parent.parentReference?.id) {
          updateParentSelectionRecursively(
            parent.parentReference.id,
            parentId,
            allTreeChildrenSelected
          );
        }
        return;
      }

      // IMPORTANT: Even if the parent didn't change, we must check upward
      // because the grandparent might need to be updated due to other changes
      if (parent.parentReference?.id) {
        updateParentSelectionRecursively(parent.parentReference.id, parentId, parent.selected);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateTeamsFile, findChildrenInTree]
  );
  // Helper function to toggle descendants
  const toggleDescendants = useCallback(
    (parentId: string, selected: boolean) => {
      updateTeamsFile(parentId, { selected });

      // Get all parents of parentId going upward
      const getAllParents = (itemId: string): string[] => {
        const parents: string[] = [];
        let currentItem = getTeamsFileById(itemId);

        while (currentItem?.parentReference?.id) {
          parents.push(currentItem.parentReference.id);
          currentItem = getTeamsFileById(currentItem.parentReference.id);
        }

        return parents;
      };
      const getAllChildren = (itemId: string): TeamsFile[] => {
        const children: TeamsFile[] = [];
        const directChildren = getTeamsFilesByParentId(itemId);

        for (const child of directChildren) {
          children.push(child);
          if (child.folder) {
            children.push(...getAllChildren(child.id));
          }
        }

        return children;
      };
      const allParents = getAllParents(parentId);
      // Check if any of those parents exists in selectedRows
      const hasParentInSelectedRows = [parentId, ...allParents].some((parentId) =>
        selectedRows.includes(parentId)
      );
      let children = getAllChildren(parentId);
      for (const folder of children.filter((child) => child.folder)) {
        toggleDescendants(folder.id, selected);
      }
      if (!hasParentInSelectedRows) {
        //We need to filter only the selected ones
        children = children.filter((child) => selectedRows.includes(child.id));
      }
      for (const child of children) {
        toggleDescendants(child.id, selected);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateTeamsFile, selectedRows]
  );

  // Simple toggle of selected - only change the state in teamsFiles
  const handleToggleItem = useCallback(
    (itemId: string, isChecked: boolean) => {
      const item = getTeamsFileById(itemId);

      if (item?.folder) {
        // If it's a folder, toggle all descendants
        toggleDescendants(itemId, isChecked);
      } else {
        // If it's a file, only toggle this file
        updateTeamsFile(itemId, { selected: isChecked });
      }

      // After updating, check if the parent should be automatically selected
      // Pass the future state to avoid synchronization issues
      if (item?.parentReference?.id) {
        updateParentSelectionRecursively(item.parentReference.id, itemId, isChecked);
      }
    },
    [getTeamsFileById, updateTeamsFile, updateParentSelectionRecursively, toggleDescendants]
  );

  // Render tree recursively
  const renderTreeNodes = useCallback(
    (nodes: TreeNode[]): React.ReactNode => {
      return nodes.map((node) => (
        <TreeViewItem
          key={node.id}
          label={node.name}
          type={node.folder ? "folder" : "file"}
          level={node.level}
          category={node.category || null}
          isChecked={node.selected}
          isIndeterminate={false}
          onCheckboxChange={(checked) => handleToggleItem(node.id, checked)}
        >
          {node.children.length > 0 && renderTreeNodes(node.children)}
        </TreeViewItem>
      ));
    },
    [handleToggleItem]
  );

  // Helper function to get all IDs from the internal tree
  const getAllTreeNodeIds = useCallback((nodes: TreeNode[]): string[] => {
    const ids: string[] = [];

    const collectIds = (nodeList: TreeNode[]) => {
      for (const node of nodeList) {
        ids.push(node.id);
        if (node.children.length > 0) {
          collectIds(node.children);
        }
      }
    };

    collectIds(nodes);
    return ids;
  }, []);

  const handleToggleAll = useCallback(
    (isChecked: boolean) => {
      // Only affect files that are in the internal tree
      const treeNodeIds = getAllTreeNodeIds(treeNodes);

      for (const fileId of treeNodeIds) {
        updateTeamsFile(fileId, { selected: isChecked });
      }
    },
    [getAllTreeNodeIds, treeNodes, updateTeamsFile]
  );

  const areAllSelected = useMemo(() => {
    // Only check files from the internal tree
    const treeNodeIds = getAllTreeNodeIds(treeNodes);
    if (treeNodeIds.length === 0) return false;

    return treeNodeIds.every((nodeId) => {
      const file = getTeamsFileById(nodeId);
      return file?.selected ?? false;
    });
  }, [getAllTreeNodeIds, treeNodes, getTeamsFileById]);

  const selectedCount = useMemo(() => {
    // Only count files from the internal tree that are selected
    const treeNodeIds = getAllTreeNodeIds(treeNodes);
    return treeNodeIds.filter((nodeId) => {
      const file = getTeamsFileById(nodeId);
      return file?.selected ?? false;
    }).length;
  }, [getAllTreeNodeIds, treeNodes, getTeamsFileById]);

  const totalCount = useMemo(() => {
    // Total files in the internal tree
    return getAllTreeNodeIds(treeNodes).length;
  }, [getAllTreeNodeIds, treeNodes]);

  const areSomeSelected = useMemo(() => {
    const treeNodeIds = getAllTreeNodeIds(treeNodes);
    const someSelected = treeNodeIds.some((nodeId) => {
      const file = getTeamsFileById(nodeId);
      return file?.selected ?? false;
    });
    return someSelected && !areAllSelected;
  }, [getAllTreeNodeIds, treeNodes, getTeamsFileById, areAllSelected]);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {showSelectAll && getAllTreeNodeIds(treeNodes).length > 0 && (
        <FormControlLabel
          control={
            <Checkbox
              checked={areAllSelected}
              indeterminate={areSomeSelected}
              onChange={(e) => handleToggleAll(e.target.checked)}
              sx={{
                padding: "0 4px",
              }}
            />
          }
          label={`Select All (${selectedCount}/${totalCount} selected)`}
          sx={{ margin: 0 }}
        />
      )}
      {showDivider && (
        <Box data-testid="tree-view-divider" sx={{ borderBottom: "1px solid #E0E0E0", mb: 1 }} />
      )}
      {getAllTreeNodeIds(treeNodes).length === 0 ? (
        <Box sx={{ padding: 2, textAlign: "center", color: "text.secondary" }}>
          No files available
        </Box>
      ) : (
        renderTreeNodes(treeNodes)
      )}
    </Box>
  );
};
